/**
 * Church Demand Intelligence Layout
 *
 * Sub-layout with tab navigation for Church Demand Intelligence.
 * Follows the OnboardingLayout pattern.
 * 4 tabs: Overview (index), Clusters, Searches, Trends.
 *
 * Note: Unlike OnboardingLayout, there is NO redirect for /church-demand.
 * The landing page IS the index route — the Overview tab uses `end` so it
 * highlights correctly only when the URL is exactly /church-demand.
 */

import { NavLink, Outlet } from 'react-router-dom';
import {
  MapPinIcon,
  MapIcon,
  ListBulletIcon,
  MagnifyingGlassIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';

const tabs = [
  { name: 'Overview', to: '', icon: MapIcon, end: true },
  { name: 'Clusters', to: 'clusters', icon: ListBulletIcon },
  { name: 'Searches', to: 'searches', icon: MagnifyingGlassIcon },
  { name: 'Trends', to: 'trends', icon: ArrowTrendingUpIcon },
];

export default function ChurchDemandLayout() {
  return (
    <div className="space-y-0">
      {/* Header */}
      <div className="bg-gradient-to-r from-deep-navy to-storm-blue text-white rounded-xl mb-6">
        <div className="px-6 py-6">
          <div className="flex items-center gap-3">
            <MapPinIcon className="h-8 w-8" />
            <div>
              <h2 className="text-2xl font-bold">Church Demand Intelligence</h2>
              <p className="text-blue-200 text-sm mt-0.5">
                Unserved church clusters from FYC searches, lead submissions, and notify-me signals
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm mb-6 sticky top-[52px] z-10">
        <nav className="flex gap-1 overflow-x-auto p-2" aria-label="Church Demand tabs">
          {tabs.map((tab) => (
            <NavLink
              key={tab.name}
              to={tab.to}
              end={tab.end}
              className={({ isActive }) =>
                `flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors min-h-[44px] ${
                  isActive
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-100'
                }`
              }
            >
              <tab.icon className="h-4 w-4" />
              {tab.name}
            </NavLink>
          ))}
        </nav>
      </div>

      {/* Page Content */}
      <Outlet />
    </div>
  );
}
